import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';


const STATUS_CONFIG = {
  open: { label: 'Open', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  resolved: { label: 'Resolved', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
  closed: { label: 'Closed', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
};

const PRIORITY_CONFIG = {
  low: { label: '🟢 Low', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  medium: { label: '🟡 Medium', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  high: { label: '🟠 High', className: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  critical: { label: '🔴 Critical', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const TYPE_CONFIG = {
  bug: { 
    label: 'Bug', 
    icon: '🐛',
    className: 'bg-red-500/10 text-red-600 border-red-500/20' 
  },
  feature: { 
    label: 'Feature', 
    icon: '✨',
    className: 'bg-purple-500/10 text-purple-600 border-purple-500/20' 
  },
  improvement: { 
    label: 'Improvement', 
    icon: '📈',
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' 
  },
};

const APPROVAL_CONFIG = {
  pending_approval: { label: 'Pending', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  approved: { label: 'Approved', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const MODULE_COLORS = {
  'Dashboard': 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  'Leads': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  'Deals': 'bg-green-500/10 text-green-600 border-green-500/20',
  'Contacts': 'bg-pink-500/10 text-pink-600 border-pink-500/20',
  'Cases': 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
  'Tasks': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  'Messages (WhatsApp)': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  'Admin': 'bg-red-500/10 text-red-600 border-red-500/20',
  'Settings': 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  'Other': 'bg-slate-500/10 text-slate-600 border-slate-500/20',
};

const DATE_LOCALES = {
  'pt-BR': ptBR,
  'en-US': enUS,
  'es': es,
};

export default function FeedbackTable({ feedbackItems, isLoading, onViewFeedback }) {
  const { t, i18n } = useTranslation();
  
  const dateLocale = DATE_LOCALES[i18n.language] || ptBR;

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">{t('productFeedback.table.loading')}</p>
      </div>
    );
  }

  if (!feedbackItems || feedbackItems.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">{t('productFeedback.table.noFeedback')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">{t('productFeedback.table.id')}</TableHead>
            <TableHead>{t('productFeedback.table.type')}</TableHead>
            <TableHead>{t('productFeedback.table.module')}</TableHead>
            <TableHead>{t('productFeedback.table.title')}</TableHead>
            <TableHead>{t('productFeedback.table.status')}</TableHead>
            <TableHead>{t('productFeedback.table.priority')}</TableHead>
            <TableHead>{t('productFeedback.table.approval')}</TableHead>
            <TableHead>{t('productFeedback.table.reportedBy')}</TableHead>
            <TableHead>{t('productFeedback.table.date')}</TableHead>
            <TableHead className="text-right">{t('productFeedback.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {feedbackItems.map((item) => (
            <TableRow key={item.id} className="cursor-pointer hover:bg-accent/50" onClick={() => onViewFeedback(item)}>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {item.id.slice(0, 8)}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={TYPE_CONFIG[item.type]?.className}>
                  {TYPE_CONFIG[item.type]?.icon} {t(`productFeedback.type.${item.type}`)}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={MODULE_COLORS[item.module] || MODULE_COLORS['Other']}>
                  {item.module}
                </Badge>
              </TableCell>
              <TableCell className="max-w-md truncate font-medium">
                {item.title}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={STATUS_CONFIG[item.status]?.className}>
                  {t(`productFeedback.status.${item.status}`)}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={PRIORITY_CONFIG[item.priority]?.className}>
                  {PRIORITY_CONFIG[item.priority]?.label?.split(' ')[0]} {t(`productFeedback.priority.${item.priority}`)}
                </Badge>
              </TableCell>
              <TableCell>
                {item.approval_status ? (
                  <Badge variant="outline" className={APPROVAL_CONFIG[item.approval_status]?.className}>
                    {t(`productFeedback.approvalStatus.${item.approval_status}`)}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground text-xs">{t('productFeedback.approvalStatus.na')}</span>
                )}
              </TableCell>
              <TableCell>
                {item.reported_by?.display_name || 
                 `${item.reported_by?.first_name} ${item.reported_by?.last_name}` ||
                 'Unknown'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(item.created_at), {
                  addSuffix: true,
                  locale: dateLocale,
                })}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewFeedback(item);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
